/**
 * The gate: loading, signed out, or in.
 *
 * WHY THERE IS NO SEPARATE SESSION REQUEST. `GET /api/me` already answers the only question that
 * matters — it returns the household or it returns 401 — so a `getSession()` call before it would
 * be a second round trip to learn something the first one tells us, plus a window in which the two
 * can disagree. `MeResult` keeps the HTTP status alongside the failure for exactly this: 401 means
 * "sign in", anything else means "we could not reach the portal", and those two must never be
 * rendered as each other. A client whose train went into a tunnel must not be told their session
 * expired and made to hunt for an email.
 *
 * THE MAGIC LINK LANDS HERE. The server mails `${PORTAL_ORIGIN}/sign-in?token=…`, so whatever
 * serves this bundle MUST fall back to index.html for unknown paths or the link 404s before any of
 * this runs. The token is exchanged for the session cookie below, then removed from the address
 * bar immediately — a single-use credential has no business sitting in browser history, in a
 * bookmark, or in the Referer header of the next request the page makes.
 */

import { useEffect, useRef, useState, type ReactElement } from 'react'

import { consumeToken, type ApiFailure } from './api/client'
import { AppShell } from './components/AppShell'
import { ErrorBoundary } from './components/ErrorBoundary'
import { Loading } from './components/Loading'
import { SignIn } from './components/SignIn'
import { PHONE_DISPLAY, PHONE_HREF } from './model/contact'
import { Icon, UI_ICON_PATHS } from './model/icons'
import { PrivacyScreen } from './screens/PrivacyScreen'
import { useMe } from './state/portal'

export function App(): ReactElement {
  return (
    <ErrorBoundary>
      <Gate />
    </ErrorBoundary>
  )
}

type LinkState =
  | { readonly state: 'none' }
  | { readonly state: 'consuming' }
  /** The server's own sentence — "expired", "already used", "not valid" are different problems. */
  | { readonly state: 'rejected'; readonly message: string }

/** The server's schema floor is 10 characters; anything shorter is not worth a request. */
function tokenFromUrl(): string | null {
  if (typeof window === 'undefined') return null
  const token = new URL(window.location.href).searchParams.get('token')
  return token !== null && token.length >= 10 ? token : null
}

function stripTokenFromUrl(): void {
  if (typeof window === 'undefined') return
  const url = new URL(window.location.href)
  if (!url.searchParams.has('token')) return
  url.searchParams.delete('token')
  // `/sign-in` exists only to carry the token, so once it is spent the history entry goes back to
  // the root — a reload or a back-button press must not re-enter a path holding a dead credential.
  const path = url.pathname === '/sign-in' ? '/' : url.pathname
  window.history.replaceState(null, '', `${path}${url.search}${url.hash}`)
}

function Gate(): ReactElement {
  const me = useMe()
  const [link, setLink] = useState<LinkState>(() => (tokenFromUrl() === null ? { state: 'none' } : { state: 'consuming' }))
  const [showPrivacy, setShowPrivacy] = useState(false)
  const attempted = useRef<string | null>(null)

  useEffect(() => {
    const token = tokenFromUrl()
    if (token === null) return

    /*
      NOT an AbortController, and the guard is a ref on purpose.

      A magic token is single-use and consuming it is atomic on the server. StrictMode mounts this
      component, runs the effect, tears it down and runs it again — so an abort-on-cleanup would
      cancel the only attempt, and an unguarded second attempt would spend the token and then be
      told by the server that the token had already been used. Both fail in development only,
      which is the worst place for a sign-in bug to hide. The ref survives the StrictMode remount
      (same fibre, same refs), so exactly one request is ever made.
    */
    if (attempted.current === token) return
    attempted.current = token

    stripTokenFromUrl()

    void consumeToken(token).then((result) => {
      if (result.ok) {
        setLink({ state: 'none' })
        // The cookie is set; ask for the record the failed first attempt could not have.
        me.refresh()
        return
      }
      if (result.error.code === 'aborted') return
      setLink({ state: 'rejected', message: result.error.message })
    })
    // Runs once, for the token the page was opened with. `me.refresh` is stable and `me` is not a
    // dependency: re-running this on every record change would re-read a URL we have already
    // emptied.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (link.state === 'consuming') return <Loading label="Signing you in…" />

  if (me.loading) return <Loading />

  if (me.status === 'error') {
    // 401 is not a fault. It is the ordinary answer for "nobody is signed in on this device".
    if (me.errorStatus === 401) {
      /*
        Local state rather than a route, because signed out there is no PortalProvider screen state
        to use and no router to add one to. The notice is a leaf: it opens over the sign-in screen
        and its own back control is the only way on, so a single boolean is the whole model.
      */
      if (showPrivacy) return <PrivacyScreen onBack={() => setShowPrivacy(false)} />
      return (
        <SignIn
          notice={link.state === 'rejected' ? link.message : null}
          onShowPrivacy={() => setShowPrivacy(true)}
        />
      )
    }
    return <Unreachable error={me.error} onRetry={me.refresh} />
  }

  return <AppShell />
}

interface UnreachableProps {
  readonly error: ApiFailure | null
  readonly onRetry: () => void
}

/**
 * The portal could not be reached, or answered with something it should not have.
 *
 * Distinct from the sign-in screen and from the error boundary, and it must stay distinct: this is
 * the only one of the three where trying again is a sensible thing to do, and the only one where
 * the right next step might be "wait for the signal to come back" rather than anything the client
 * has done wrong. `requestId` is shown when the server sent one — it is the difference between
 * "it broke" and a support call that can find the actual request.
 */
function Unreachable({ error, onRetry }: UnreachableProps): ReactElement {
  return (
    <main
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        padding: '26px 18px calc(26px + env(safe-area-inset-bottom))',
      }}
    >
      <section
        className="card elev-sm"
        style={{
          width: 'min(430px, 100%)',
          alignItems: 'center',
          textAlign: 'center',
          gap: 0,
          padding: '36px 26px',
          animation: 'ft-rise .4s ease both',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            background: 'var(--color-neutral-200)',
            color: 'var(--color-neutral-700)',
            marginBottom: '14px',
          }}
        >
          <Icon paths={UI_ICON_PATHS.message} size={24} />
        </span>
        <h1 style={{ margin: '0 0 6px', fontSize: '26px' }}>We could not load your record</h1>
        <p role="alert" className="text-muted" style={{ maxWidth: '40ch', margin: '0 0 20px', fontSize: '14px', lineHeight: 1.55 }}>
          {error?.message ?? 'Something went wrong at our end. Please try again in a moment.'}
        </p>
        <button type="button" className="btn btn-primary" onClick={onRetry} style={{ minHeight: '44px' }}>
          Try again
        </button>
        {/*
          The number shows whether or not the server sent a requestId. Gating the whole line on
          `requestId` — as this did — meant that the failures which never reach our server at all,
          the ones where a client most needs a human, were the exact failures that offered no way
          to reach one.
        */}
        <p className="text-muted" style={{ margin: '18px 0 0', fontSize: '12.5px', lineHeight: 1.55 }}>
          If it keeps failing, ring us on{' '}
          <a href={PHONE_HREF} style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
            {PHONE_DISPLAY}
          </a>
          .{error?.requestId === undefined ? null : <> Please quote {error.requestId}.</>}
        </p>
      </section>
    </main>
  )
}
